import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import useApi from "../utils/useApi";

const DEFAULT_MIN_HOUR = 9;
const DEFAULT_MAX_HOUR = 19;
const MAX_RANGE_DAYS = 30;

const parseHourBound = (timeValue, isCloseBound = false) => {
  if (!timeValue || typeof timeValue !== "string") return null;

  const [rawHour, rawMinute, rawSecond] = timeValue.split(":");
  const hour = Number(rawHour);
  const minute = Number(rawMinute || 0);
  const second = Number(rawSecond || 0);

  if (!Number.isFinite(hour)) return null;

  let normalizedHour = Math.max(0, Math.min(23, hour));
  if (isCloseBound && (minute > 0 || second > 0)) {
    normalizedHour = Math.min(23, normalizedHour + 1);
  }

  return normalizedHour;
};

const getWeekAnchorDates = (startDate, endDate) => {
  const anchors = [];
  let cursor = dayjs(startDate);
  const end = dayjs(endDate);

  while (cursor.isBefore(end) || cursor.isSame(end, "day")) {
    anchors.push(cursor.format("YYYY-MM-DD"));
    cursor = cursor.add(7, "day");
  }

  return anchors;
};

const getDatesSetInRange = (startDate, endDate) => {
  const dates = new Set();
  let cursor = dayjs(startDate);
  const end = dayjs(endDate);

  while (cursor.isBefore(end) || cursor.isSame(end, "day")) {
    dates.add(cursor.format("YYYY-MM-DD"));
    cursor = cursor.add(1, "day");
  }

  return dates;
};

export default function useDateRangeWorkingHours(selectedDateRange) {
  const { getWeeklySchedule } = useApi();
  const [startDate, endDate] = Array.isArray(selectedDateRange)
    ? selectedDateRange
    : [null, null];

  const hasValidRange = Boolean(startDate && endDate);

  const { data, isLoading } = useQuery({
    queryKey: ["date-range-working-hours", startDate, endDate],
    queryFn: async () => {
      if (!hasValidRange) return null;

      const start = dayjs(startDate);
      const end = dayjs(endDate);
      if (!start.isValid() || !end.isValid()) return null;

      const daysSpan = end.diff(start, "day") + 1;
      if (daysSpan <= 0) return null;

      const boundedEnd =
        daysSpan > MAX_RANGE_DAYS ? start.add(MAX_RANGE_DAYS - 1, "day") : end;

      const anchors = getWeekAnchorDates(start, boundedEnd);
      const rangeDatesSet = getDatesSetInRange(start, boundedEnd);

      const scheduleResponses = await Promise.all(
        anchors.map((anchorDate) => getWeeklySchedule(anchorDate))
      );

      const byDate = new Map();
      scheduleResponses.forEach((response) => {
        const weeklySchedule = response?.weekly_schedule || [];
        weeklySchedule.forEach((item) => {
          if (item?.date && rangeDatesSet.has(item.date)) {
            byDate.set(item.date, item);
          }
        });
      });

      let minHour = null;
      let maxHour = null;

      byDate.forEach((scheduleItem) => {
        const openHour = parseHourBound(scheduleItem.open_time, false);
        const closeHour = parseHourBound(scheduleItem.close_time, true);
        if (
          openHour === null ||
          closeHour === null ||
          closeHour <= openHour ||
          (openHour === 0 && closeHour === 0)
        ) {
          return;
        }

        minHour = minHour === null ? openHour : Math.min(minHour, openHour);
        maxHour = maxHour === null ? closeHour : Math.max(maxHour, closeHour);
      });

      if (minHour === null || maxHour === null || maxHour <= minHour) {
        return null;
      }

      return { minHour, maxHour };
    },
    enabled: hasValidRange,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    minHour: data?.minHour ?? DEFAULT_MIN_HOUR,
    maxHour: data?.maxHour ?? DEFAULT_MAX_HOUR,
    isLoading,
  };
}
